import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ChevronDown, MapPin } from "lucide-react";
import { useLang } from "./LangContext";
import { scrollToId } from "../scroll";
import { EASE } from "./Reveal";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 16 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 16 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const go = (branch) => {
    scrollToId("subeler");
    if (branch) window.dispatchEvent(new CustomEvent("mdr:highlight-branch", { detail: branch }));
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} className="hero" data-testid="hero-section">
      <motion.div className="hero-bg" style={{ y: bgY }} aria-hidden="true">
        <img src="/img/hero_arena.jpeg" alt="" fetchPriority="high" decoding="async" />
      </motion.div>
      <div className="hero-veil" aria-hidden="true" />

      <motion.div className="wrap hero-grid">
        <div className="hero-copy">
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            data-testid="hero-eyebrow"
          >
            <MapPin size={13} aria-hidden="true" /> {t.hero.eyebrow}
          </motion.p>

          <h1 className="hero-title font-display" data-testid="hero-title">
            {t.hero.titleLines.map((line, i) => (
              <span className="mask-line" key={line}>
                <motion.span
                  className={i === t.hero.accentLineIndex ? "accent" : ""}
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.05, delay: 0.24 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="hero-sub"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.62, ease: EASE }}
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            className="hero-ctas"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.76, ease: EASE }}
          >
            <button className="btn btn-primary" data-testid="hero-cta-lefkosa" onClick={() => go("lefkosa")}>
              {t.hero.ctaLefkosa}
            </button>
            <button className="btn btn-primary" data-testid="hero-cta-gazimagusa" onClick={() => go("gazimagusa")}>
              {t.hero.ctaMagusa}
            </button>
            <button className="btn btn-outline" data-testid="hero-cta-locations" onClick={() => go(null)}>
              {t.hero.ctaLocations}
            </button>
          </motion.div>
        </div>

        <motion.div
          className="hero-emblem"
          style={{ rotateX: rX, rotateY: rY, transformPerspective: 900 }}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
        >
          <div className="emblem-ring" aria-hidden="true" />
          <div className="emblem-glow" aria-hidden="true" />
          <img src="/logo.png" alt={t.hero.logoAlt} data-testid="hero-logo" />
        </motion.div>
      </motion.div>

      <motion.div
        className="hero-scroll"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        data-testid="hero-scroll-cue"
      >
        <span>{t.hero.scroll}</span>
        <ChevronDown size={16} className="bounce-y" aria-hidden="true" />
      </motion.div>
    </section>
  );
}
